import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUserCreditBalance } from '../hooks/useUserData';
import { useUserAutoDepositSettings } from '../hooks/useUserData';
import { useAuthProvider } from '../utils/auth';

const CreditWarningContext = createContext();

export const useCreditWarning = () => {
  const context = useContext(CreditWarningContext);
  if (!context) {
    throw new Error('useCreditWarning must be used within a CreditWarningProvider');
  }
  return context;
};

export const CreditWarningProvider = ({ children }) => {
  const [warnings, setWarnings] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const { isAuthed, isLoading: authLoading } = useAuthProvider();
  
  // Only fetch data if user is authenticated
  const { data: creditBalance, isLoading: balanceLoading } = useUserCreditBalance({
    enabled: isAuthed && !authLoading
  });
  const { data: autoDepositSettings, isLoading: settingsLoading } = useUserAutoDepositSettings({
    enabled: isAuthed && !authLoading
  });

  useEffect(() => {
    // Don't process warnings if user is not authenticated or still loading
    if (!isAuthed || authLoading || balanceLoading || settingsLoading || !creditBalance || !autoDepositSettings) {
      return;
    }

    const newWarnings = [];
    
    // Check low credit warning
    if (creditBalance <= autoDepositSettings.low_credit_threshold) {
      newWarnings.push({
        type: 'low_credit',
        severity: 'warning',
        title: 'Low Credit Balance',
        message: `Your credit balance is $${creditBalance.toFixed(2)}. Consider adding more credit to continue listening.`,
        action: 'add_credit'
      });
    }
    
    // Check critical low credit (below $0.50)
    if (creditBalance <= 0.50) {
      newWarnings.push({
        type: 'critical_credit',
        severity: 'error',
        title: 'Critical Low Credit',
        message: `Your credit balance is very low ($${creditBalance.toFixed(2)}). You may not be able to continue listening.`,
        action: 'add_credit_urgent'
      });
    }
    
    // Check if auto-deposit is enabled and user is below threshold
    if (autoDepositSettings.is_enabled && 
        autoDepositSettings.frequency !== 'manual' && 
        creditBalance <= autoDepositSettings.low_credit_threshold) {
      newWarnings.push({
        type: 'auto_deposit_triggered',
        severity: 'info',
        title: 'Auto-Deposit Triggered',
        message: 'Your credit balance has triggered an automatic deposit based on your settings.',
        action: 'none'
      });
    }

    setWarnings(newWarnings);
    setShowWarning(newWarnings.length > 0);
  }, [creditBalance, autoDepositSettings, balanceLoading, settingsLoading, isAuthed, authLoading]);

  const dismissWarning = (warningType) => {
    setWarnings(prev => prev.filter(w => w.type !== warningType));
    if (warnings.length === 1) {
      setShowWarning(false);
    }
  };

  const dismissAllWarnings = () => {
    setWarnings([]);
    setShowWarning(false);
  };

  const value = {
    warnings,
    showWarning,
    dismissWarning,
    dismissAllWarnings,
    creditBalance,
    autoDepositSettings
  };

  return (
    <CreditWarningContext.Provider value={value}>
      {children}
    </CreditWarningContext.Provider>
  );
};
